/**
 * Development Keyboard Shortcuts Hook
 * BRD-DEV-001 | US-DEV-006
 *
 * Provides keyboard shortcuts for quick persona switching in development mode.
 *
 * Shortcuts:
 * - Ctrl+Shift+1 → Owner
 * - Ctrl+Shift+2 → Admin
 * - Ctrl+Shift+3 → User
 * - Ctrl+Shift+4 → Viewer
 * - Ctrl+Shift+5 → Advisor (BRD-019: consolidated professional/intermediary)
 * - Ctrl+Shift+D → Open persona switcher page
 *
 * Note: All shortcuts are disabled in production.
 */

import { useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';

// BRD-019: Consolidated professional/intermediary into advisor
type PersonaType = 'owner' | 'admin' | 'user' | 'viewer' | 'advisor';

const PERSONA_SHORTCUTS: Record<string, PersonaType> = {
  '1': 'owner',
  '2': 'admin',
  '3': 'user',
  '4': 'viewer',
  '5': 'advisor',
};

const PERSONA_REDIRECTS: Record<PersonaType, string> = {
  owner: '/dashboard',
  admin: '/dashboard',
  user: '/dashboard',
  viewer: '/dashboard',
  advisor: '/advisor/dashboard',
};

interface UseDevKeyboardShortcutsOptions {
  enabled?: boolean;
  onSwitch?: (persona: PersonaType) => void;
}

export function useDevKeyboardShortcuts(options: UseDevKeyboardShortcutsOptions = {}) {
  const { enabled = true, onSwitch } = options;
  const navigate = useNavigate();

  // Only enable in development mode
  const isDevMode = import.meta.env.DEV && import.meta.env.VITE_DEV_AUTH_BYPASS === 'true';

  const switchPersona = useCallback(
    async (persona: PersonaType) => {
      try {
        const response = await fetch(`/api/v1/dev/switch/${persona}`, {
          method: 'POST',
          credentials: 'include',
        });

        if (!response.ok) {
          console.error(`[DevKeyboardShortcuts] Failed to switch to ${persona}`);
          return;
        }

        const data = await response.json();

        // Store token
        localStorage.setItem('myvault_access_token', data.token);

        // Callback
        if (onSwitch) {
          onSwitch(persona);
        }

        // Navigate to persona's default route
        const redirectTo = PERSONA_REDIRECTS[persona] || '/dashboard';

        // Refresh to apply new session
        window.location.href = redirectTo;
      } catch (error) {
        console.error('[DevKeyboardShortcuts] Switch failed:', error);
      }
    },
    [onSwitch]
  );

  const handleKeyDown = useCallback(
    (event: KeyboardEvent) => {
      // Skip if not in dev mode or disabled
      if (!isDevMode || !enabled) return;

      // Skip if user is typing in an input
      const target = event.target as HTMLElement;
      if (
        target.tagName === 'INPUT' ||
        target.tagName === 'TEXTAREA' ||
        target.isContentEditable
      ) {
        return;
      }

      // Check for Ctrl+Shift modifier
      if (event.ctrlKey && event.shiftKey) {
        // Ctrl+Shift+D → Open persona switcher
        if (event.key === 'D' || event.key === 'd') {
          event.preventDefault();
          navigate('/dev');
          return;
        }

        // Ctrl+Shift+[1-6] → Switch persona
        const persona = PERSONA_SHORTCUTS[event.key];
        if (persona) {
          event.preventDefault();
          console.log(`[DevKeyboardShortcuts] Switching to ${persona}`);
          switchPersona(persona);
        }
      }
    },
    [isDevMode, enabled, navigate, switchPersona]
  );

  useEffect(() => {
    if (!isDevMode || !enabled) return;

    document.addEventListener('keydown', handleKeyDown);
    console.log('[DevKeyboardShortcuts] Keyboard shortcuts enabled');

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isDevMode, enabled, handleKeyDown]);

  return {
    isEnabled: isDevMode && enabled,
    shortcuts: PERSONA_SHORTCUTS,
  };
}
